import { DEFAULT_DISPLAY_PARTICIPANTS, type ActivityLocationType } from '@funkey/constants';
import { I18N_CONF, ParsedLocale } from '@funkey/locales/config';
import type * as nuqs from 'nuqs';
import {
    parseAsArrayOf,
    parseAsFloat,
    parseAsIndex,
    parseAsInteger,
    parseAsIsoDate,
    parseAsString,
    parseAsStringLiteral,
    parseAsBoolean,
} from 'nuqs/server';
import type { ActivitySearchPage } from './server';
import {
    // ACTIVITY_ACCESSIBILITY_FEATURES,  // commented out (re-enable after merge)
    ACTIVITY_CLEANLINESS_TYPES,
    ACTIVITY_LOCATION_FILTER_TYPES,
    ACTIVITY_LOCATION_TYPES,
    // ACTIVITY_TARGET_AUDIENCES,        // commented out (re-enable after merge)
    SEASONS,
} from '@funkey/constants';
import type { TRPC_ROUTER } from '~/modules/trpc/routers';
import { Pagination_Args, std, ts } from '@funkey/shared';
import type { usePortalCtx } from '~/features/global/portal';

/** URL + client state options shared by activity search nuqs hooks (filters, mode, …). */
export const ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS: nuqs.Options = {
    scroll: false,
    shallow: true,
    clearOnDefault: true,
    throttleMs: 300,
};

/** Search UX mode: standard filters vs AI-assisted search (parallel track, not part of filter parsers). */
const ACTIVITY_SEARCH_MODES = ['normal', 'ai'] as const;
export type ActivitySearchMode = (typeof ACTIVITY_SEARCH_MODES)[number];

export const ACTIVITY_SEARCH_MODE_QUERY_PARAMS = {
    mode: parseAsStringLiteral(ACTIVITY_SEARCH_MODES).withDefault('normal'),
    /** Deep-link / Cmd+K: pre-fills the AI prompt once, then cleared from the URL. */
    startPrompt: parseAsString,
} as const;

/** Floor for the per-card `minParticipants` placeholder badge — used by every
 * activity grid that renders `<ActivityCard>`s when the user hasn't picked a
 * participant filter. */
export const MIN_PARTICIPANTS_FALLBACK = 20;

/** A contiguous run of participant counts whose per-person price sits inside the
 * budget. `to` is `null` when the run continues past the last known size. */
export type AffordableParticipantRange = { from: number; to: number | null };

/** The participant counts an activity's per-person price stays within budget for.
 *
 * Scale-priced activities ("Prijsschaal") add the *last applicable* rule's
 * `extraPrice` (opstartkosten) as a flat fee, so the fee steps up at every tier
 * boundary while the per-participant rate only creeps down. Per-person price is
 * therefore a **sawtooth**, not a monotonic curve: it spikes at each boundary
 * and decays until the next one. Affordability is consequently a *set of
 * ranges*, not a single boolean — an activity can fit the budget at 9-20, break
 * it at 21-28, fit again at 29-30, break at 31-42 and fit from 43 (FUN-4983).
 *
 * Judging affordability at one hardcoded party size therefore produces both
 * false negatives (hidden although bookable at the size the bizz books) and
 * unexplained prices (shown, then over budget at the size the user picks).
 *
 * `priceRange` maps participant count to the VAT-exclusive per-person price.
 * Sizes are only considered inside the `[window.min, window.max]` the bizz is
 * scoped to. Returns `[]` when nothing fits. */
export function resolveAffordableParticipantRanges(args: {
    priceRange: Record<number, number> | null | undefined;
    budget: { min?: number | null; max?: number | null };
    window?: { min?: number | null; max?: number | null } | null;
}): AffordableParticipantRange[] {
    if (!args.priceRange) return [];

    const sizes = Object.keys(args.priceRange)
        .map(Number)
        .filter(size => Number.isFinite(size))
        .filter(size => typeof args.window?.min !== 'number' || size >= args.window.min)
        .filter(size => typeof args.window?.max !== 'number' || size <= args.window.max)
        .sort((a, b) => a - b);

    const ranges: AffordableParticipantRange[] = [];
    let open: { from: number; to: number } | null = null;

    for (const size of sizes) {
        const price = args.priceRange[size];
        const affordable =
            typeof price === 'number' &&
            (typeof args.budget.min !== 'number' || price >= args.budget.min) &&
            (typeof args.budget.max !== 'number' || price <= args.budget.max);

        if (!affordable) {
            if (open) ranges.push(open);
            open = null;
            continue;
        }
        // Only extend the open run when sizes are consecutive; a gap in the
        // price map is a hole we must not paper over.
        if (open && size === open.to + 1) open.to = size;
        else {
            if (open) ranges.push(open);
            open = { from: size, to: size };
        }
    }
    if (open) ranges.push(open);

    const lastSize = sizes[sizes.length - 1];
    const last = ranges[ranges.length - 1];
    // An open-ended tail: affordable at the largest size we know about and not
    // capped by the bizz window, so it keeps holding as the group grows.
    if (last && lastSize !== undefined && last.to === lastSize && args.window?.max == null) {
        return [...ranges.slice(0, -1), { from: last.from, to: null }];
    }
    return ranges;
}

/** `[{from:9,to:20},{from:43,to:null}]` → `"9-20, 43+"`. Formatting only — the
 * caller supplies the surrounding sentence and translation. */
export function formatParticipantRanges(ranges: readonly AffordableParticipantRange[]): string {
    return ranges
        .map(range => {
            if (range.to === null) return `${range.from}+`;
            return range.from === range.to ? `${range.from}` : `${range.from}-${range.to}`;
        })
        .join(', ');
}

export const ACTIVITY_SORT_QUERY_PARAMS = {
    sort: parseAsStringLiteral([
        'popularity.DESC',
        // 'name.ASC',
        // 'name.DESC',
        'price.ASC',
        'price.DESC',
        'createdAt.ASC',
        'createdAt.DESC',
        'distance.ASC',
    ]),
    /** Center coords for distance sort. Set by the region city-search input; required
     * for `sort=distance.ASC` to do anything (otherwise the sort is silently dropped). */
    sortCenterLat: parseAsFloat,
    sortCenterLng: parseAsFloat,
};

const ACTIVITY_FILTER_SEARCH_QUERY_PARAMS = {
    query: parseAsString,
} as const;

export const ACTIVITY_FILTER_PAGINATION_QUERY_PARAMS = {
    page: parseAsIndex.withDefault(0),
};

/** Debug params for testing */
export const ACTIVITY_BOOST_AD_QUERY_PARAMS = {
    /** Show only ads (for debugging) - use ?debugAdsOnly=true */
    debugAdsOnly: parseAsBoolean.withDefault(false),
    /**
     * Override date for ad position testing - use ?debugAdDate=2024-01-15
     * This simulates what ads would look like on a different day.
     * Useful for verifying that ad positions rotate daily.
     */
    debugAdDate: parseAsIsoDate,
};

/** Query params for the main activity filter bar */
export const ACTIVITY_FILTER_BAR_QUERY_PARAMS = {
    // Budget
    minBudget: parseAsFloat,
    maxBudget: parseAsFloat,

    participants: parseAsInteger,

    // Regions
    regions: parseAsArrayOf(parseAsInteger).withDefault([]),
    countryCode: parseAsStringLiteral(I18N_CONF.COUNTRIES),

    // Categories
    categories: parseAsArrayOf(parseAsInteger).withDefault([]),
    subCategories: parseAsArrayOf(parseAsInteger).withDefault([]),

    // City/region distance search (DB-layer, not Elastic)
    nearLat: parseAsFloat,
    nearLng: parseAsFloat,
    nearLabel: parseAsString,
    radius: parseAsInteger,

    // Route filter (FIXED = "I go to the partner", CUSTOM = "partner comes to me").
    // Lives in the filter bar (location popover), so it must serialize from the hero search.
    location: parseAsArrayOf(parseAsStringLiteral(ACTIVITY_LOCATION_FILTER_TYPES)).withDefault([]),

    nearCleared: parseAsBoolean,
} as const;

export function NEAR_POINT_DEFAULTS(args: {
    latitude?: string | number | null;
    longitude?: string | number | null;
    label: string | undefined;
    radiusKm: number;
}): { nearLat: number; nearLng: number; nearLabel: string; radius: number } | undefined {
    const nearLat = toCoordinate(args.latitude);
    const nearLng = toCoordinate(args.longitude);
    if (nearLat === null || nearLng === null) return undefined;
    if (!args.label) return undefined;

    return { nearLat, nearLng, nearLabel: args.label, radius: args.radiusKm };
}

function toCoordinate(value: string | number | null | undefined): number | null {
    if (typeof value === 'number') return globalThis.Number.isFinite(value) ? value : null;

    return std.Number.safeParseFloat(value ?? null);
}

/** Filter drawer filters */
const _ACTIVITY_FILTER_DRAWER_EXTRA_QUERY_PARAMS = {
    languages: parseAsArrayOf(parseAsInteger).withDefault([]),

    minDuration: parseAsInteger,
    maxDuration: parseAsInteger,

    locationType: parseAsArrayOf(parseAsStringLiteral(ACTIVITY_LOCATION_TYPES)).withDefault([]),
    season: parseAsArrayOf(parseAsStringLiteral(SEASONS)).withDefault([]),
    cleanliness: parseAsArrayOf(parseAsStringLiteral(ACTIVITY_CLEANLINESS_TYPES)).withDefault([]),
    teamwork: parseAsArrayOf(parseAsInteger).withDefault([]),
    action: parseAsArrayOf(parseAsInteger).withDefault([]),
    competition: parseAsArrayOf(parseAsInteger).withDefault([]),
    learning: parseAsArrayOf(parseAsInteger).withDefault([]),

    // accessibility: parseAsArrayOf(                              // commented out (re-enable after merge)
    //     parseAsStringLiteral(ACTIVITY_ACCESSIBILITY_FEATURES),
    // ).withDefault([]),
    // targetAudience: parseAsArrayOf(parseAsStringLiteral(ACTIVITY_TARGET_AUDIENCES)).withDefault([]),
} as const;

const QUERY_PARAM_PARSERS = {
    ...ACTIVITY_SORT_QUERY_PARAMS,
    ...ACTIVITY_FILTER_SEARCH_QUERY_PARAMS,
    ...ACTIVITY_FILTER_BAR_QUERY_PARAMS,
    ..._ACTIVITY_FILTER_DRAWER_EXTRA_QUERY_PARAMS,
} as const;
export type Activity_Search_QueryParams = nuqs.Values<typeof QUERY_PARAM_PARSERS>;
export namespace Activity_Search_QueryParams {
    export type DefaultValueOverrides = ts.Object.require<
        Partial<{
            [K in keyof Activity_Search_QueryParams]: NonNullable<Activity_Search_QueryParams[K]>;
        }>,
        'countryCode' /* country code always needs to be set as default value */
    >;

    /** @internal */
    export function _createParsers(
        defaultValueOverrides: Activity_Search_QueryParams.DefaultValueOverrides,
    ) {
        const parsers = {
            ...QUERY_PARAM_PARSERS,
        };

        const OVERRIDES = std.Object.entries(defaultValueOverrides);
        for (let i = 0; i < OVERRIDES.length; i++) {
            const [key, newDefaultValue] = OVERRIDES[i]!;
            if (newDefaultValue === undefined) continue;

            key satisfies keyof typeof parsers;

            // @ts-ignore TS is being annoying here due to some parsers maybe not being nullable anymore. But it works
            parsers[key] = parsers[key].withDefault(newDefaultValue);
        }

        return parsers;
    }
}
export function _ACTIVITY_SEARCH_QUERY_OPTIONS(
    options: ts.Object.require<ActivitySearchPage.SearchCustomizationProps, 'weightSet'> & {
        locale: I18N_CONF.Locale;
        vatRate: number | null;
        bizzID: number | null;

        weightSet: ActivitySearchPage.SearchCustomizationProps['weightSet'];
        includeAds: ActivitySearchPage.SearchCustomizationProps['includeAds'];
        /** See `withFacetCounts` on the search procedure. Server and client must
         * agree on it, or the SSR payload and the client query are two different
         * cache keys and the page refetches on mount. */
        withFacetCounts?: boolean;
    },
    // prettier-ignore
    params:
        & Partial<Activity_Search_QueryParams>
        & Partial<nuqs.Values<typeof ACTIVITY_BOOST_AD_QUERY_PARAMS>>
        & {
            pagination: Pagination_Args;
            activityIDs?: number[];
            groupIDs?: number[];
            /** Region IDs ordered closest-first; results matching earlier IDs are surfaced first. */
            prioritizeRegionIDs?: number[];
        },
): TRPC_ROUTER.input<typeof TRPC_ROUTER.activities.search> {
    const parsedLocale = ParsedLocale.fromLocale(options.locale);
    if (params.countryCode) parsedLocale.country = params.countryCode;

    const hasNearPoint = !params.nearCleared && params.nearLat != null && params.nearLng != null;

    // NOTE: this is the whole query key (used for client side caching + prefetching on server).
    // So it's important to NEVER EVER spread objects into this. Or else we might accidentally change the key.
    return {
        locale: parsedLocale.toString(),
        weightSet: options.weightSet,

        pagination: params.pagination,
        vatRate: options.vatRate,

        query: params.query || null,
        withFacetCounts: options.withFacetCounts ?? false,

        near: hasNearPoint ? { latitude: params.nearLat, longitude: params.nearLng } : null,
        radius: (hasNearPoint && params.radius) || null,

        filters: (() => {
            // When the caller (server.tsx prefetch / client grid query) passes
            // a non-empty `activityIDs` list it means we are running an AI
            // search and the pgvector hit set should hard-filter the grid.
            // The set itself now lives in sessionStorage (keyed by chat id)
            // rather than in the URL — the caller is responsible for reading
            // the store and forwarding the ids here.
            const activityIDs = nullIfEmpty(params.activityIDs);
            const isAiHardFilter = activityIDs !== null;

            return {
                activityIDs,
                groupIDs: nullIfEmpty(params.groupIDs ?? options.groupIDs),

                // When the pgvector hit set is locked in, App Search must NOT
                // re-filter by participants/budget — those constraints were
                // already applied during the semantic retrieval, and layering
                // them on top of an ID whitelist would drop legitimate matches.
                // We still want `participants` in the URL because the cards
                // use it for the price-display label.
                //
                // The default group size filters too, even though it is never
                // written to the URL: the header says the page is priced for it,
                // so the results have to be activities that can take it. Without
                // this the page offered five-to-twelve activities to a visitor it
                // was quoting for twenty, and they could only read "price on
                // request". Bizz keeps its own unfiltered list.
                participants: isAiHardFilter
                    ? null
                    : params.participants ||
                      (options.bizzID === null ? DEFAULT_DISPLAY_PARTICIPANTS : null),
                minBudget: isAiHardFilter ? null : params.minBudget || null,
                maxBudget: isAiHardFilter ? null : params.maxBudget || null,

                languages: nullIfEmpty(params.languages),
                location: nullIfEmpty(params.location),
                locationType: nullIfEmpty(params.locationType),
                action: nullIfEmpty(params.action),
                season: nullIfEmpty(params.season),
                regions: nullIfEmpty(params.regions),
                learning: nullIfEmpty(params.learning),
                teamwork: nullIfEmpty(params.teamwork),
                categories: nullIfEmpty(params.categories),
                cleanliness: nullIfEmpty(params.cleanliness),
                competition: nullIfEmpty(params.competition),
                minDuration: params.minDuration || null,
                maxDuration: params.maxDuration || null,
                subCategories: nullIfEmpty(params.subCategories),
                // accessibility: nullIfEmpty(params.accessibility),       // commented out (re-enable after merge)
                // targetAudience: nullIfEmpty(params.targetAudience),   // commented out (re-enable after merge)
            } satisfies Record<string, string | number | null | [any, ...rest: any[]]>;
        })(),

        prioritizeRegionIDs: nullIfEmpty(params.prioritizeRegionIDs),

        constraints: options.constraints ?? undefined,

        orderBy:
            (() => {
                if (!params.sort) return null;

                /** Filter out popularity.DESC to use relevance scoring instead, this is the default sort
                 * sorting on `popularity.DESC` would bypass the relevance scoring and ONLY use the popularity score instead
                 */
                if (params.sort === 'popularity.DESC') return undefined;

                const split = params.sort.split(
                    '.',
                ) as typeof params.sort extends `${infer Criteria}.${infer Direction}`
                    ? [Criteria, Direction]
                    : never;
                if (split.length !== 2) return null;

                const [criteria, direction] = split;

                if (criteria === 'distance') {
                    // Distance sort needs a center coord. If the city-search hasn't set one,
                    // silently fall back to default (popularity) — UI should disable the option,
                    // but URL tampering shouldn't crash the resolver.
                    if (params.sortCenterLat == null || params.sortCenterLng == null) {
                        return undefined;
                    }
                    return {
                        criteria,
                        direction,
                        center: { lat: params.sortCenterLat, lng: params.sortCenterLng },
                    };
                }

                return { criteria, direction };
            })() ?? undefined,

        ...(options.includeAds
            ? {
                  includeAds: true,
                  debugAdsOnly: params.debugAdsOnly ?? undefined,
                  debugAdDate: params.debugAdDate ?? undefined,
              }
            : { includeAds: false }),
    };
}

function nullIfEmpty<T>(arr: T[] | undefined | null): [T, ...rest: T[]] | null {
    return !arr?.length ? null : (arr as [T, ...rest: T[]]);
}

export function ActivityFilterResults_calculatePagination(
    portal: usePortalCtx.Portal,
    pageIdx: number,
): Pagination_Args {
    const PAGE_SIZE = 6 * 4;

    return portal === 'WEB'
        ? {
              // Handle USP card on 1st page
              limit: PAGE_SIZE - (pageIdx === 0 ? 1 : 0),
              offset: PAGE_SIZE * pageIdx - (pageIdx > 0 ? 1 : 0),
          }
        : {
              size: PAGE_SIZE,
              pageIdx,
          };
}

/** `min`/`max` come back from the duration aggregate as numeric strings, and a
 * variant may state no duration at all — both collapse to `null` so the card
 * omits the row rather than showing "0 min". */
export function parseDurationMinutes(row: {
    minDurationMinutes: string | number | null;
    maxDurationMinutes: string | number | null;
}): { min: number; max: number } | null {
    const min = Number(row.minDurationMinutes);
    const max = Number(row.maxDurationMinutes);
    if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0) return null;
    return { min, max };
}

/** `avg` is a numeric string and `count` a bigint string. No approved review
 * means `null`, which the card reads as "render no stars" — zero stars would say
 * "rated 0 out of 5" rather than "not rated yet". */
export function parseReviews(row: {
    reviewAverage: string | number | null;
    reviewCount: string | number;
}): { average: number; count: number } | null {
    const count = Number(row.reviewCount);
    const average = Number(row.reviewAverage);
    if (!Number.isFinite(count) || count <= 0 || !Number.isFinite(average)) return null;
    return { average, count };
}

export type DurationParts = { hours: number; minutes: number };

/** How long an activity takes, in the shape a card or a pill can render.
 *
 * Whole hours read as hours and anything under an hour as minutes. A length
 * that falls between the two used to read as "2,5 uur", which is a sum the
 * reader has to do; `mixed` hands back the hours and minutes separately so the
 * caller can say "2u 30m" in its own language. */
export function formatDurationRange(
    locale: string,
    duration: { min: number; max: number },
): {
    unit: 'minutes' | 'hours' | 'mixed';
    min: string;
    max: string;
    minParts: DurationParts;
    maxParts: DurationParts;
} {
    const parts = (minutes: number): DurationParts => ({
        hours: Math.floor(minutes / 60),
        minutes: minutes % 60,
    });
    const minParts = parts(duration.min);
    const maxParts = parts(duration.max);

    if (duration.max < 60) {
        return {
            unit: 'minutes',
            min: String(duration.min),
            max: String(duration.max),
            minParts,
            maxParts,
        };
    }

    if (minParts.minutes === 0 && maxParts.minutes === 0) {
        const format = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
        return {
            unit: 'hours',
            min: format.format(duration.min / 60),
            max: format.format(duration.max / 60),
            minParts,
            maxParts,
        };
    }

    return {
        unit: 'mixed',
        min: String(duration.min),
        max: String(duration.max),
        minParts,
        maxParts,
    };
}

/** What a card is allowed to say about price, from the Functional Analysis:
 * the figure is the cheapest variant, so it reads as a starting price when the
 * activity has several; and a group size the price rules do not cover gets no
 * figure at all rather than one nobody can book.
 *
 * The total is derived here from the same participant count the price was
 * calculated at, so a card's total and the group size stated above the results
 * cannot describe different queries. */
/**
 * The group size a card can actually quote.
 *
 * Prices exist only for the sizes an activity serves, and the results page asks
 * every card for one size — the default unless the visitor typed another. A four
 * to twelve activity has no price at twenty, so it read "price on request" while
 * its own page quoted the twelve it can take. Asking each activity for the
 * nearest size it does serve makes the two pages agree; the card then says the
 * price starts there.
 */
export function resolvePricedParticipants(args: {
    displaySize: number;
    minParticipants: number;
    maxParticipants: number | null;
}): number {
    if (args.displaySize < args.minParticipants) return args.minParticipants;
    if (args.maxParticipants !== null && args.displaySize > args.maxParticipants) {
        return args.maxParticipants;
    }
    return args.displaySize;
}

export function resolveCardPrice(args: {
    price: number;
    /** Whether `price` is the real per-person price for `participants`, rather
     * than a floor left over from a size the price rules do not cover. */
    priceIsForDisplaySize: boolean;
    hasMultipleVariants: boolean;
    participants: number;
    /** Whether `participants` is the size the visitor asked for, or the nearest
     * one this activity serves. A substituted size is a floor, so it reads
     * "from" the same way several variants do. */
    isSubstitutedSize?: boolean;
}): { kind: 'on-request' } | { kind: 'amount'; perPerson: number; total: number; isFrom: boolean } {
    if (args.price <= 0 || !args.priceIsForDisplaySize) return { kind: 'on-request' };
    return {
        kind: 'amount',
        perPerson: args.price,
        total: args.price * args.participants,
        isFrom: args.hasMultipleVariants || args.isSubstitutedSize === true,
    };
}

/** From this group size a self-service search stops being the best route, so
 * the results offer a person instead. One above the default of 20: a visitor
 * who never touched the filter is not pushed at the contact form. */
export const PROMOTION_MIN_PARTICIPANTS = 21;

/** Which non-activity card, if any, takes a slot in the results. The control
 * group keeps the USP card; the reworked page moves the USPs above the header
 * and puts the promotion band after the first row once the group is large
 * enough. */
export function resolveInjectedCard(args: {
    portal: 'WEB' | 'BIZZ' | 'ADMIN' | 'PARTNER';
    isFinetuning: boolean;
    participants: number;
}): 'usp' | 'promotion' | null {
    if (args.portal !== 'WEB') return null;
    if (!args.isFinetuning) return 'usp';
    return args.participants >= PROMOTION_MIN_PARTICIPANTS ? 'promotion' : null;
}

/** Which variant of the PLP experiment this render shows, in precedence order.
 *
 * The server resolves the flag from the visitor cookie and the page paints that
 * answer, so it is the one the visitor is already looking at. The browser client
 * is asked only when the server had nothing — otherwise a late-arriving client
 * value swaps the layout under someone mid-read, which is the shift this whole
 * arrangement exists to avoid. */
export function resolvePlpVariant(args: {
    /** `?plp=finetuning` or `?plp=control`, for reviewing both on one URL. */
    override: string | null;
    /** What the page was rendered with, `null` when the server could not say. */
    server: 'finetuning' | 'control' | null;
    /** What the browser client says, `null` until PostHog has loaded. */
    client: 'finetuning' | 'control' | null;
    /** Where the client has nothing to go on either. */
    fallback: 'finetuning' | 'control';
}): 'finetuning' | 'control' {
    if (args.override === 'finetuning' || args.override === 'control') return args.override;
    return args.server ?? args.client ?? args.fallback;
}

/** The participants filter never stores the default: typing the default group
 * size, or anything unusable, reads as "not set", so the URL only carries a
 * value the visitor chose. */
export function withoutDefaultParticipants(people: number | null | undefined): number | null {
    if (people == null || !Number.isFinite(people) || people < 1) return null;
    return people === DEFAULT_DISPLAY_PARTICIPANTS ? null : people;
}

/** Results per filter option, counted on the list the page renders. Only the
 * facets whose values sit on the search row can be counted this way. */
export type FacetCounts = {
    categories: Record<number, number>;
    locationType: Partial<Record<ActivityLocationType, number>>;
    languages: Record<number, number>;
};

/**
 * PLP URL that opens the hero title as the search input, focused. An empty
 * `query` param is what the title reads as "search mode"; the header's search
 * entry links here instead of opening the search overlay.
 */
export function plpSearchEntryUrl(locale: I18N_CONF.Locale) {
    return `${I18N_CONF.getTranslatedUrl('teambuildings', { locale })}?query=`;
}
